import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const OUTBOX_KEY = "neo.attendance.outbox.v1";
const ACTIVE_PREFIX = "neo.attendance.active.v1";

const safeJsonParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const cleanKey = (value = "") =>
  String(value || "").trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "-");

const activeKey = (orgId, storeId, staffName) =>
  `${ACTIVE_PREFIX}.${cleanKey(orgId)}.${cleanKey(storeId)}.${cleanKey(staffName)}`;

export function makeAttendanceId(prefix = "evt") {
  const random =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  return `${prefix}_${Date.now()}_${random}`.replace(/[^a-zA-Z0-9_-]/g, "_");
}

export function getAttendanceOutbox() {
  try {
    const parsed = safeJsonParse(localStorage.getItem(OUTBOX_KEY), []);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveAttendanceOutbox(items) {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(items || []));
  } catch {}
}

export function enqueueAttendanceOperation(operation) {
  const op = {
    ...operation,
    id: operation.id || makeAttendanceId(operation.type || "attendance"),
    createdAtMs: operation.createdAtMs || Date.now(),
    attempts: Number(operation.attempts || 0),
  };
  const queue = getAttendanceOutbox();
  const next = [...queue.filter((item) => item.id !== op.id), op]
    .sort((a, b) => Number(a.createdAtMs || 0) - Number(b.createdAtMs || 0));
  saveAttendanceOutbox(next);
  return op;
}

export function cacheLocalOpenShift(orgId, storeId, staffName, shift) {
  if (!orgId || !storeId || !staffName || !shift?.id) return;
  try {
    localStorage.setItem(
      activeKey(orgId, storeId, staffName),
      JSON.stringify({
        ...shift,
        cachedAtMs: Date.now(),
      })
    );
  } catch {}
}

export function getLocalOpenShift(orgId, storeId, staffName) {
  if (!orgId || !storeId || !staffName) return null;
  try {
    const value = safeJsonParse(
      localStorage.getItem(activeKey(orgId, storeId, staffName)),
      null
    );
    if (!value?.id) return null;
    return value;
  } catch {
    return null;
  }
}

export function clearLocalOpenShift(orgId, storeId, staffName) {
  try {
    localStorage.removeItem(activeKey(orgId, storeId, staffName));
  } catch {}
}

const syncNeoAttendanceOperation = httpsCallable(
  functions,
  "syncNeoAttendanceOperation"
);

const callableSafeOperation = (op = {}) => {
  const extra = op.extraShiftData && typeof op.extraShiftData === "object"
    ? { ...op.extraShiftData }
    : {};

  const capturedAt = extra.clockInPhotoCapturedAt;
  if (capturedAt?.toMillis) {
    extra.clockInPhotoCapturedAtMs = capturedAt.toMillis();
  } else if (capturedAt?.seconds) {
    extra.clockInPhotoCapturedAtMs = Number(capturedAt.seconds) * 1000;
  }
  delete extra.clockInPhotoCapturedAt;

  return {
    ...op,
    extraShiftData: extra,
  };
};

export async function syncAttendanceOperationNow(operation) {
  const result = await syncNeoAttendanceOperation(callableSafeOperation(operation));
  return result?.data || {};
}

async function syncClockIn(op) {
  return syncAttendanceOperationNow(op);
}

async function syncClockOut(op) {
  return syncAttendanceOperationNow(op);
}

let flushing = false;

export async function flushAttendanceOutbox() {
  if (flushing) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;

  flushing = true;
  try {
    let queue = getAttendanceOutbox();

    while (queue.length) {
      const op = queue[0];
      try {
        if (op.type === "clock_in") {
          await syncClockIn(op);
        } else if (op.type === "clock_out") {
          await syncClockOut(op);
        } else {
          throw new Error(`Unknown attendance operation: ${op.type}`);
        }

        queue = getAttendanceOutbox().filter((item) => item.id !== op.id);
        saveAttendanceOutbox(queue);
      } catch (error) {
        // Keep the operation durable. A later online/interval flush retries it.
        const current = getAttendanceOutbox();
        const next = current.map((item) =>
          item.id === op.id
            ? {
                ...item,
                attempts: Number(item.attempts || 0) + 1,
                lastAttemptAtMs: Date.now(),
                lastError: String(error?.message || error || "sync failed"),
              }
            : item
        );
        saveAttendanceOutbox(next);
        break;
      }
    }
  } finally {
    flushing = false;
  }
}
